/**
 * Filter Presets & Types
 *
 * Defines filter settings, presets, and overlay configurations
 * for the image editor
 */

// ==================== Filter Settings ====================

/**
 * Adjustable filter values
 * All values are in their CSS filter units
 */
export interface FilterSettings {
  brightness: number // 0-200, default 100 (percentage)
  contrast: number // 0-200, default 100 (percentage)
  saturation: number // 0-200, default 100 (percentage)
  hueRotate: number // 0-360, default 0 (degrees)
  blur: number // 0-10, default 0 (pixels)
  grayscale: number // 0-100, default 0 (percentage)
  sepia: number // 0-100, default 0 (percentage)
}

/**
 * Default filter settings (no changes)
 */
export const DEFAULT_FILTERS: FilterSettings = {
  brightness: 100,
  contrast: 100,
  saturation: 100,
  hueRotate: 0,
  blur: 0,
  grayscale: 0,
  sepia: 0,
}

/**
 * Convert filter settings to CSS filter string
 */
export function filterSettingsToCSS(filters: FilterSettings): string {
  const parts: string[] = []

  if (filters.brightness !== 100) {
    parts.push(`brightness(${filters.brightness}%)`)
  }
  if (filters.contrast !== 100) {
    parts.push(`contrast(${filters.contrast}%)`)
  }
  if (filters.saturation !== 100) {
    parts.push(`saturate(${filters.saturation}%)`)
  }
  if (filters.hueRotate !== 0) {
    parts.push(`hue-rotate(${filters.hueRotate}deg)`)
  }
  if (filters.blur > 0) {
    parts.push(`blur(${filters.blur}px)`)
  }
  if (filters.grayscale > 0) {
    parts.push(`grayscale(${filters.grayscale}%)`)
  }
  if (filters.sepia > 0) {
    parts.push(`sepia(${filters.sepia}%)`)
  }

  return parts.length > 0 ? parts.join(' ') : 'none'
}

// ==================== Filter Presets ====================

export interface FilterPreset {
  id: string
  name: string
  filters: Partial<FilterSettings>
  overlay?: OverlayType
}

/**
 * Pre-defined filter presets
 */
export const FILTER_PRESETS: FilterPreset[] = [
  {
    id: 'original',
    name: 'Original',
    filters: {},
  },
  {
    id: 'vivid',
    name: 'Vivid',
    filters: {
      saturation: 130,
      contrast: 110,
    },
  },
  {
    id: 'warm',
    name: 'Warm',
    filters: {
      sepia: 20,
      brightness: 105,
      saturation: 110,
    },
  },
  {
    id: 'cool',
    name: 'Cool',
    filters: {
      hueRotate: 15,
      brightness: 105,
      saturation: 90,
    },
  },
  {
    id: 'bw',
    name: 'B&W',
    filters: {
      grayscale: 100,
      contrast: 110,
    },
  },
  {
    id: 'vintage',
    name: 'Vintage',
    filters: {
      sepia: 40,
      contrast: 90,
      brightness: 105,
    },
    overlay: 'vignette-light',
  },
  {
    id: 'dramatic',
    name: 'Dramatic',
    filters: {
      contrast: 130,
      brightness: 90,
      saturation: 110,
    },
  },
  {
    id: 'soft',
    name: 'Soft',
    filters: {
      contrast: 85,
      brightness: 110,
      blur: 0.3,
    },
  },
  {
    id: 'moody',
    name: 'Moody',
    filters: {
      brightness: 90,
      contrast: 115,
      saturation: 80,
    },
    overlay: 'vignette-strong',
  },
  {
    id: 'golden',
    name: 'Golden',
    filters: {
      sepia: 30,
      brightness: 110,
      contrast: 105,
      saturation: 120,
    },
  },
  {
    id: 'faded',
    name: 'Faded',
    filters: {
      contrast: 80,
      saturation: 70,
      brightness: 115,
    },
  },
  {
    id: 'bold',
    name: 'Bold',
    filters: {
      contrast: 120,
      saturation: 140,
    },
  },
]

/**
 * Apply a preset to get full filter settings
 */
export function applyPreset(preset: FilterPreset): FilterSettings {
  return {
    ...DEFAULT_FILTERS,
    ...preset.filters,
  }
}

// ==================== Overlays ====================

export type OverlayType =
  | 'none'
  | 'vignette-light'
  | 'vignette-strong'
  | 'warm-gradient'
  | 'cool-gradient'
  | 'golden-hour'
  | 'sunset'
  | 'duotone-purple'
  | 'duotone-teal'

export interface OverlayConfig {
  id: OverlayType
  name: string
  css: string // CSS background/gradient
  blendMode: string // CSS mix-blend-mode
  opacity: number // 0-1
}

/**
 * Overlay configurations
 */
export const OVERLAYS: OverlayConfig[] = [
  {
    id: 'none',
    name: 'None',
    css: 'transparent',
    blendMode: 'normal',
    opacity: 0,
  },
  {
    id: 'vignette-light',
    name: 'Vignette Light',
    css: 'radial-gradient(circle, transparent 50%, rgba(0,0,0,0.3) 100%)',
    blendMode: 'multiply',
    opacity: 1,
  },
  {
    id: 'vignette-strong',
    name: 'Vignette Strong',
    css: 'radial-gradient(circle, transparent 30%, rgba(0,0,0,0.6) 100%)',
    blendMode: 'multiply',
    opacity: 1,
  },
  {
    id: 'warm-gradient',
    name: 'Warm Tint',
    css: 'linear-gradient(180deg, rgba(255,200,100,0.2) 0%, rgba(255,150,50,0.3) 100%)',
    blendMode: 'overlay',
    opacity: 1,
  },
  {
    id: 'cool-gradient',
    name: 'Cool Tint',
    css: 'linear-gradient(180deg, rgba(100,150,255,0.2) 0%, rgba(50,100,200,0.3) 100%)',
    blendMode: 'overlay',
    opacity: 1,
  },
  {
    id: 'golden-hour',
    name: 'Golden Hour',
    css: 'linear-gradient(135deg, rgba(255,180,50,0.3) 0%, transparent 60%)',
    blendMode: 'overlay',
    opacity: 1,
  },
  {
    id: 'sunset',
    name: 'Sunset',
    css: 'linear-gradient(180deg, rgba(255,100,50,0.2) 0%, rgba(255,50,100,0.2) 50%, rgba(100,50,150,0.2) 100%)',
    blendMode: 'overlay',
    opacity: 1,
  },
  {
    id: 'duotone-purple',
    name: 'Duotone Purple',
    css: 'linear-gradient(180deg, rgba(100,50,150,0.4) 0%, rgba(200,100,150,0.4) 100%)',
    blendMode: 'color',
    opacity: 0.6,
  },
  {
    id: 'duotone-teal',
    name: 'Duotone Teal',
    css: 'linear-gradient(180deg, rgba(0,100,100,0.4) 0%, rgba(50,150,150,0.4) 100%)',
    blendMode: 'color',
    opacity: 0.6,
  },
]

/**
 * Get overlay config by ID
 */
export function getOverlayConfig(id: OverlayType): OverlayConfig {
  return OVERLAYS.find((o) => o.id === id) || OVERLAYS[0]
}
